import React from 'react';
import { useInfiniteScroll } from '@/hooks';

interface InfiniteScrollListProps {
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  children: React.ReactNode;
  emptyStateComponent?: React.ReactNode;
  className?: string;
  threshold?: number;
}

const InfiniteScrollList: React.FC<InfiniteScrollListProps> = ({
  loading,
  loadingMore,
  error,
  hasMore,
  onLoadMore,
  children,
  emptyStateComponent,
  className = 'max-h-[calc(100vh-300px)] overflow-y-auto',
  threshold = 0.8,
}) => {
  const scrollContainerRef = useInfiniteScroll({
    loading: loadingMore,
    hasMore,
    onLoadMore,
    threshold,
  });

  if (loading) {
    return (
      <div className="w-full border border-gray-200 rounded-lg p-15 text-center shadow-sm bg-white">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Loading...
          </h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full border border-red-200 rounded-lg p-15 text-center shadow-sm bg-red-50">
        <div className="flex flex-col items-center">
          <h3 className="text-lg font-semibold text-red-900 mb-1">
            Error loading data
          </h3>
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className={`${className} ${className.includes('scrollbar-hide') ? 'scrollbar-hide' : ''}`}
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      {children}

      {/* Loading more indicator */}
      {loadingMore && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
          <span className="ml-2 text-sm text-gray-600">Loading more...</span>
        </div>
      )}

      {/* End of list indicator */}
      {!loadingMore && hasMore && (
        <div className="text-center py-4 text-sm text-gray-500">
          Scroll down to load more
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && emptyStateComponent}
    </div>
  );
};

export default InfiniteScrollList;
